import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Altimeter — GPS-only. Per pushback: browser barometric sensor access
// via GenericSensor is effectively unavailable in current browsers (iOS
// Safari has none; Chrome Android stopped exposing pressure for privacy
// reasons). So this is a GPS altimeter.
//
// GPS altitude has poor accuracy compared to horizontal (often ±20-100 m
// vertical even with good horizontal lock). We display this honestly.
//
// Optional baseline mode: user marks a reference point, then sees their
// elevation change relative to that point — much more useful than the
// absolute number (e.g. for hiking, the gain/loss matters more).

const M_TO_FT = 3.28084;

export default function Altimeter() {
  const [state, setState] = useState("idle");
  const [pos, setPos] = useState(null);
  const [unit, setUnit] = useState("m");
  const [baseline, setBaseline] = useState(null);
  const watchRef = useRef(null);

  const enable = () => {
    if (!navigator.geolocation) { setState("unsupported"); return; }
    setState("requesting");
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => {
        setPos({
          altitude: p.coords.altitude,
          accuracy: p.coords.altitudeAccuracy,
          horizontalAccuracy: p.coords.accuracy,
        });
        setState("ok");
      },
      () => setState("denied"),
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 30000 }
    );
  };

  useEffect(() => () => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
  }, []);

  const showAlt = (m) => {
    if (m == null) return "—";
    return unit === "m" ? `${m.toFixed(0)} m` : `${(m * M_TO_FT).toFixed(0)} ft`;
  };
  const showDelta = (m) => {
    if (m == null) return "—";
    const v = unit === "m" ? m : m * M_TO_FT;
    return `${v >= 0 ? "+" : ""}${v.toFixed(0)} ${unit === "m" ? "m" : "ft"}`;
  };

  const noAltitude = pos && pos.altitude == null;
  const delta = baseline != null && pos && pos.altitude != null ? pos.altitude - baseline : null;

  return (
    <ToolsShell title="Altimeter">
      <div style={{ background: "#1F1418", border: "1px solid #E0584E33", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#D9A39E", marginBottom: 16 }}>
        <strong>GPS-only altimeter.</strong> Browser barometric pressure access is unavailable on most devices, so this uses GPS altitude — typically ±20–50 m vertical accuracy at best. Use the baseline feature to track relative gain/loss, which is what GPS does well.
      </div>

      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 10 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 16 }}>
            Tap to enable location and read your GPS altitude.
          </p>
          <button onClick={enable} style={primaryBtn} disabled={state === "requesting"}>
            {state === "requesting" ? "Getting GPS lock…" : "Enable location"}
          </button>
          <SensorNotice state={state === "idle" || state === "requesting" ? "prompt" : state} what="Location" />
        </div>
      )}

      {state === "ok" && pos && (
        <>
          <div style={{ display: "flex", gap: 6, marginBottom: 14, justifyContent: "center" }}>
            {[{ id: "m", label: "Meters" }, { id: "ft", label: "Feet" }].map((u) => (
              <button
                key={u.id}
                onClick={() => setUnit(u.id)}
                style={{ background: unit === u.id ? TOOLS_ACCENT : "#1C1E2E", color: unit === u.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
              >
                {u.label}
              </button>
            ))}
          </div>

          {noAltitude ? (
            <div style={{ background: "#13141F", border: "1px solid #262839", borderRadius: 12, padding: "20px", textAlign: "center", color: "#D9A39E" }}>
              GPS altitude isn't available on this device or browser.
            </div>
          ) : (
            <>
              <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 14, padding: "24px 22px", textAlign: "center" }}>
                <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Altitude (above sea level)</div>
                <div style={{ fontSize: 56, fontWeight: 800, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                  {showAlt(pos.altitude)}
                </div>
                {pos.accuracy != null && (
                  <div style={{ fontSize: 12, color: "#9A9DBE", marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
                    ± {unit === "m" ? pos.accuracy.toFixed(0) : (pos.accuracy * M_TO_FT).toFixed(0)} {unit}
                  </div>
                )}
              </div>

              {baseline != null && (
                <div style={{ background: "#13141F", border: `1px solid ${(delta || 0) >= 0 ? "#5BD99A" : "#F5A299"}33`, borderRadius: 12, padding: "16px 20px", marginTop: 10, textAlign: "center" }}>
                  <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Change from baseline</div>
                  <div style={{ fontSize: 32, fontWeight: 700, color: (delta || 0) >= 0 ? "#5BD99A" : "#F5A299", fontVariantNumeric: "tabular-nums" }}>
                    {showDelta(delta)}
                  </div>
                  <div style={{ fontSize: 11, color: "#7C8099", marginTop: 4 }}>
                    Baseline set at {showAlt(baseline)}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "center", flexWrap: "wrap" }}>
                <button onClick={() => setBaseline(pos.altitude)} style={{ background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Set baseline here
                </button>
                {baseline != null && (
                  <button onClick={() => setBaseline(null)} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
                    Clear baseline
                  </button>
                )}
              </div>
            </>
          )}

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
            Use the baseline feature for hiking gain/loss — GPS measures *changes* in altitude more reliably than absolute elevation.
          </p>
        </>
      )}
    </ToolsShell>
  );
}
